import {
  Component,
  Input,
  ChangeDetectionStrategy,
  booleanAttribute,
  computed,
  input,
} from '@angular/core';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'success' | 'warning';
export type ButtonSize    = 'sm' | 'md' | 'lg';

// ─── Variant map ──────────────────────────────────────────────────────────────
// Tailwind v4 classes: usa tokens definidos em @theme (styles.css)
const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:   'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 focus-visible:ring-brand-400',
  secondary: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 active:bg-slate-100 focus-visible:ring-slate-400',
  danger:    'bg-rose-600 text-white hover:bg-rose-700 active:bg-rose-800 focus-visible:ring-rose-400',
  ghost:     'text-slate-600 hover:bg-slate-100 active:bg-slate-200 focus-visible:ring-slate-400',
  success:   'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 focus-visible:ring-emerald-400',
  warning:   'bg-amber-500 text-white hover:bg-amber-600 active:bg-amber-700 focus-visible:ring-amber-400',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-7 px-3 text-xs gap-1.5',
  md: 'h-9 px-4 text-sm gap-2',
  lg: 'h-11 px-6 text-base gap-2',
};

const ICON_SIZE: Record<ButtonSize, string> = {
  sm: 'w-3.5 h-3.5',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
};

/**
 * @usage
 * <ui-button variant="primary" size="md" [loading]="saving()">Salvar</ui-button>
 * <ui-button variant="danger"  (click)="cancelar()">Cancelar</ui-button>
 * <ui-button variant="success" fullWidth [disabled]="!valido()">Finalizar Venda [F8]</ui-button>
 *
 * Acessibilidade:
 * - Spread `ariaLabel` quando o conteúdo textual não é descritivo (ícone apenas)
 * - `role="status"` no spinner informa leitores de tela do estado de carregamento
 */
@Component({
  selector: 'ui-button',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'inline-block',
    '[attr.data-variant]': 'variant',
  },
  template: `
    <button
      [type]="type"
      [disabled]="disabled || loading"
      [class]="classes()"
      [attr.aria-label]="ariaLabel"
      [attr.aria-busy]="loading || null"
    >
      <!-- Spinner de carregamento — anuncia "ocupado" para leitores de tela -->
      @if (loading) {
        <svg
          [class]="iconClass()"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          class="animate-spin shrink-0"
        >
          <circle class="opacity-25" cx="12" cy="12" r="10"
            stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
        </svg>
        <span role="status" class="sr-only">Aguarde…</span>
      }

      <ng-content />
    </button>
  `,
})
export class UiButtonComponent {
  @Input() variant: ButtonVariant = 'primary';
  @Input() size:    ButtonSize    = 'md';
  @Input() type:    'button' | 'submit' | 'reset' = 'button';
  @Input() buttonClass = '';
  @Input({ transform: booleanAttribute }) loading   = false;
  @Input({ transform: booleanAttribute }) disabled  = false;
  @Input({ transform: booleanAttribute }) fullWidth = false;
  @Input() ariaLabel?: string;

  classes = computed(() => [
    'inline-flex items-center justify-center font-medium rounded-lg',
    'transition-all duration-[120ms] select-none cursor-pointer',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'btn-pulse',
    SIZE_CLASSES[this.size],
    VARIANT_CLASSES[this.variant],
    this.fullWidth ? 'w-full' : '',
    this.buttonClass,
  ].filter(Boolean).join(' '));

  iconClass = computed(() => `shrink-0 ${ICON_SIZE[this.size]}`);
}
